export type PollContext = {
  isFirstRun: boolean;
  // No further retry will follow, whatever this run returns
  isLastAttempt: boolean;
};

export type Poller = {
  name: string;
  // null = runs only once, right after login
  intervalMs: number | null;
  // Only for pollers without an interval: how many later ticks may retry a failed first run
  retryAttempts?: number;
  isEnabled: () => boolean;
  run: (context: PollContext) => Promise<unknown>;
};

const TICK_INTERVAL_MS = 1000 * 60;

// Slack for timer jitter, so a retry is not skipped when a tick fires slightly early
const MIN_RETRY_GAP_MS = TICK_INTERVAL_MS / 2;

let tickTimer: ReturnType<typeof setInterval> | null = null;
let activePollers: Poller[] = [];
const lastRunAtByPoller = new Map<string, number>();
const retriesLeftByPoller = new Map<string, number>();
const runningPollers = new Set<string>();

const isLastAttempt = (poller: Poller, isFirstRun: boolean) =>
  poller.intervalMs !== null ||
  !poller.retryAttempts ||
  (!isFirstRun && retriesLeftByPoller.get(poller.name) === 1);

const runPollers = async (pollers: Poller[], isFirstRun: boolean) => {
  const contexts = pollers.map((poller) => ({
    isFirstRun,
    isLastAttempt: isLastAttempt(poller, isFirstRun),
  }));

  pollers.forEach((poller) => runningPollers.add(poller.name));

  const results = await Promise.allSettled(
    pollers.map((poller, index) => poller.run(contexts[index])),
  );

  pollers.forEach((poller) => runningPollers.delete(poller.name));

  results.forEach((result, index) => {
    const poller = pollers[index];

    if (result.status === "fulfilled") {
      retriesLeftByPoller.delete(poller.name);

      return;
    }

    console.error(`Poller "${poller.name}" failed`, result.reason);

    if (poller.intervalMs !== null || !poller.retryAttempts) {
      return;
    }

    const retriesLeft = isFirstRun
      ? poller.retryAttempts
      : (retriesLeftByPoller.get(poller.name) ?? 0) - 1;

    if (retriesLeft > 0) {
      retriesLeftByPoller.set(poller.name, retriesLeft);
    } else {
      retriesLeftByPoller.delete(poller.name);
    }
  });
};

const runDuePollers = () => {
  if (document.visibilityState !== "visible") {
    return;
  }

  const now = Date.now();

  const duePollers = activePollers.filter((poller) => {
    if (!poller.isEnabled() || runningPollers.has(poller.name)) {
      return false;
    }

    const sinceLastRun = now - (lastRunAtByPoller.get(poller.name) ?? 0);

    if (retriesLeftByPoller.has(poller.name)) {
      return sinceLastRun >= MIN_RETRY_GAP_MS;
    }

    return poller.intervalMs !== null && sinceLastRun >= poller.intervalMs;
  });

  duePollers.forEach((poller) => lastRunAtByPoller.set(poller.name, now));

  void runPollers(duePollers, false);
};

export const stopPolling = () => {
  if (tickTimer !== null) {
    clearInterval(tickTimer);
    tickTimer = null;
  }

  document.removeEventListener("visibilitychange", runDuePollers);

  activePollers = [];
  lastRunAtByPoller.clear();
  retriesLeftByPoller.clear();
  runningPollers.clear();
};

export const startPolling = async (pollers: Poller[]) => {
  stopPolling();

  activePollers = pollers;

  const now = Date.now();
  const initialPollers = pollers.filter((poller) => poller.isEnabled());

  initialPollers.forEach((poller) => lastRunAtByPoller.set(poller.name, now));

  if (!import.meta.env.DEV) {
    tickTimer = setInterval(runDuePollers, TICK_INTERVAL_MS);
    document.addEventListener("visibilitychange", runDuePollers);
  }

  await runPollers(initialPollers, true);
};
